import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  erpFetch,
  isErpApiSuccess,
  getErpRequestErrorMessage,
  getActiveBranchId,
  listBankAccounts,
} from "../../erpClient";
import Pagination from "../../components/Pagination";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { formatIndianRupee } from "../../utils/formatLiveRate";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";

const PAYMENT_MODES = ["CASH", "UPI", "CARD", "BANK", "CHEQUE", "SKIPPED", "LATE_FEE"];
const ENTRY_TYPES = ["", "PAYMENT", "SKIP", "LATE_FEE"];
const TODAY = new Date().toISOString().split("T")[0];
const ITEMS_PER_PAGE = 15;

function EntryTypeBadge({ type }) {
  switch (type) {
    case "PAYMENT":
      return <span className="badge bg-success-subtle text-success">PAYMENT</span>;
    case "SKIP":
      return <span className="badge bg-warning-subtle text-warning">SKIP</span>;
    case "LATE_FEE":
      return <span className="badge bg-danger-subtle text-danger">LATE_FEE</span>;
    default:
      return <span className="badge bg-light text-dark">{type || "—"}</span>;
  }
}

// ─── Entry Edit Modal ─────────────────────────────────────────────────────────

function EntryEditModal({
  show,
  onHide,
  entry,
  bankAccounts,
  valueModeIsGoldWeight,
  onSave,
}) {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [form, setForm] = useState({
    receipt_date: "",
    amount: "",
    payment_mode: "CASH",
    bank_account_id: "",
    gold_rate: "",
    remarks: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setForm({
        receipt_date: entry.receipt_date?.split("T")[0] || TODAY,
        amount: entry.amount ?? "",
        payment_mode: entry.payment_mode || "CASH",
        bank_account_id: entry.bank_account_id ?? "",
        gold_rate: entry.gold_rate ?? "",
        remarks: entry.remarks ?? "",
      });
    }
  }, [entry]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        type: "update_entry",
        entry_id: entry.id,
        receipt_date: form.receipt_date,
        remarks: form.remarks,
        payment_mode: form.payment_mode,
      };
      if (form.amount !== "") payload.amount = Number(form.amount);
      if (form.bank_account_id) payload.bank_account_id = Number(form.bank_account_id);
      if (valueModeIsGoldWeight && form.gold_rate !== "") payload.gold_rate = Number(form.gold_rate);

      await onSave(payload);
      onHide();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div>
          <Modal.Title className="fs-5 fw-semibold mb-0">
            Edit Payment Entry
          </Modal.Title>
          <div className="text-muted small mt-1">
            Card #{entry?.card_no} — Installment #{entry?.installment_no} ({entry?.customer_name})
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSave}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Receipt Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.receipt_date}
                  onChange={(e) => setF("receipt_date", e.target.value)}
                  className={buttonClass}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.amount}
                      onChange={(e) => setF("amount", e.target.value)}
                      className={buttonClass}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold small">Payment Mode</Form.Label>
                    <Form.Select
                      value={form.payment_mode}
                      onChange={(e) => setF("payment_mode", e.target.value)}
                      className={buttonClass}
                    >
                      {PAYMENT_MODES.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {["BANK", "CHEQUE", "UPI", "CARD"].includes(form.payment_mode) && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Bank Account</Form.Label>
                      <Form.Select
                        value={form.bank_account_id}
                        onChange={(e) => setF("bank_account_id", e.target.value)}
                        className={buttonClass}
                      >
                        <option value="">— Select Bank Account —</option>
                        {bankAccounts.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.bank_name} - {b.account_number}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                {valueModeIsGoldWeight && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold small">Gold Rate (₹/gm)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min={0}
                        value={form.gold_rate}
                        onChange={(e) => setF("gold_rate", e.target.value)}
                        className={buttonClass}
                      />
                    </Form.Group>
                  </Col>
                )}
            <Col md={12}>
              <Form.Group>
                <Form.Label className="fw-semibold small">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.remarks}
                  onChange={(e) => setF("remarks", e.target.value)}
                  className={buttonClass}
                  placeholder="Optional entry remarks..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
          <Button variant="secondary" className={buttonClass} onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" className={buttonClass} type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── SchemePaymentTransaction Component ───────────────────────────────────────

function SchemePaymentTransaction() {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const canEdit = hasPermission(PERMISSIONS.SCHEME_EDIT);

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    dateFrom: "",
    dateTo: "",
    paymentMode: "",
    entryType: "",
  });

  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);

  const [editEntry, setEditEntry] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Track gold weight mode per plan
  const planById = useRef({});

  // Load plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const branchId = getActiveBranchId() || 1;
        const res = await erpFetch("/scheme_plan", {
          method: "POST",
          body: { type: "list", status: 1, limit: 100, branch_id: branchId },
        });
        if (isErpApiSuccess(res)) {
          const items = res.data?.items ?? res.data ?? [];
          setPlans(items);
          items.forEach((p) => {
            planById.current[p.id] = p;
          });
          if (items.length > 0) setSelectedPlanId(String(items[0].id));
        }
      } catch {
        // ignore
      }
    };
    fetchPlans();
  }, []);

  // Load bank accounts
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const branchId = getActiveBranchId() || 1;
        const res = await listBankAccounts({ branch_id: branchId });
        const items = res?.data?.items ?? res?.data ?? [];
        setBankAccounts(Array.isArray(items) ? items : []);
      } catch {
        setBankAccounts([]);
      }
    };
    fetchBanks();
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!selectedPlanId) {
      setAllEntries([]);
      return;
    }
    setLoading(true);
    try {
      const branchId = getActiveBranchId() || 1;
      // Step 1: list enrollments for the plan
      const listRes = await erpFetch("/scheme", {
        method: "POST",
        body: {
          type: "list",
          plan_id: Number(selectedPlanId),
          search: filters.search,
          status: "",
          page: 1,
          limit: 500,
          branch_id: branchId,
        },
      });

      if (!isErpApiSuccess(listRes)) {
        toast.error(listRes?.message || "Failed to load cards.");
        setAllEntries([]);
        setLoading(false);
        return;
      }

      const enrollments = listRes.data?.items ?? listRes.data ?? [];
      if (!enrollments.length) {
        setAllEntries([]);
        setLoading(false);
        return;
      }

      // Step 2: fetch each card's entries (batched via Promise.all)
      const selectedPlan = planById.current[Number(selectedPlanId)];
      const planName = selectedPlan?.name ?? "";

      const showResponses = await Promise.all(
        enrollments.slice(0, 100).map((enrollment) =>
          erpFetch("/scheme", {
            method: "POST",
            body: { type: "show", id: enrollment.id },
          }).catch(() => null)
        )
      );

      const flat = [];
      showResponses.forEach((res) => {
        if (!res || !isErpApiSuccess(res)) return;
        const card = res.data;
        const enrollment = card?.enrollment ?? card;
        const entries = Array.isArray(card?.entries) ? card.entries : [];
        entries.forEach((entry) => {
          flat.push({
            ...entry,
            plan_name: planName,
            card_no: enrollment?.card_no,
            customer_name: enrollment?.customer_name,
            enrollment_id: enrollment?.id,
            value_mode: selectedPlan?.value_mode,
          });
        });
      });

      setAllEntries(flat);
      setCurrentPage(1);
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Failed to load transactions."));
    } finally {
      setLoading(false);
    }
  }, [selectedPlanId, filters.search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Client-side filter
  const filtered = allEntries.filter((e) => {
    if (filters.entryType && e.entry_type !== filters.entryType) return false;
    if (filters.paymentMode && e.payment_mode !== filters.paymentMode) return false;
    if (filters.dateFrom && (e.receipt_date || "") < filters.dateFrom) return false;
    if (filters.dateTo && (e.receipt_date || "") > filters.dateTo) return false;
    return true;
  });

  const totalItems = filtered.length;
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const handleEntryUpdate = async (payload) => {
    try {
      const res = await erpFetch("/scheme", { method: "POST", body: payload });
      if (isErpApiSuccess(res)) {
        toast.success("Entry updated successfully.");
        fetchEntries();
      } else {
        toast.error(res?.message || "Update failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Update failed."));
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (
      !window.confirm(
        `Delete this ${entry.entry_type} entry for card #${entry.card_no} (inst #${entry.installment_no})?`
      )
    )
      return;
    setDeletingId(entry.id);
    try {
      const res = await erpFetch("/scheme", {
        method: "POST",
        body: { type: "delete_entry", entry_id: entry.id },
      });
      if (isErpApiSuccess(res)) {
        toast.success("Entry deleted successfully.");
        fetchEntries();
      } else {
        toast.error(res?.message || "Delete failed.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Delete failed."));
    } finally {
      setDeletingId(null);
    }
  };

  const editEntryValueMode = editEntry
    ? planById.current[Number(selectedPlanId)]?.value_mode === "GOLD_WEIGHT"
    : false;

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""}`}>
          {/* Header */}
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="card-title mb-0">Scheme Transactions</h4>
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn btn-outline-secondary ${buttonClass}`}
                onClick={fetchEntries}
                disabled={loading}
              >
                <Icon
                  icon="solar:refresh-line-duotone"
                  className={`me-1 ${loading ? "spin" : ""}`}
                  width={18}
                />
                Refresh
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="card-body p-4">
            {/* Filter Section */}
            <div className="row g-2 mb-3 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label mb-1 small fw-semibold text-muted">Scheme Plan</label>
                <select
                  className={`form-select ${buttonClass}`}
                  value={selectedPlanId}
                  onChange={(e) => {
                    setSelectedPlanId(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">— Select Plan —</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-3">
                <label className="form-label mb-1 small fw-semibold text-muted">
                  Search (Customer / Card)
                </label>
                <input
                  type="search"
                  className={`form-control ${buttonClass}`}
                  value={filters.search}
                  onChange={(e) => {
                    setFilter("search", e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search name, mobile, card..."
                />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label mb-1 small fw-semibold text-muted">From Date</label>
                <input
                  type="date"
                  className={`form-control ${buttonClass}`}
                  value={filters.dateFrom}
                  onChange={(e) => {
                    setFilter("dateFrom", e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label mb-1 small fw-semibold text-muted">To Date</label>
                <input
                  type="date"
                  className={`form-control ${buttonClass}`}
                  value={filters.dateTo}
                  onChange={(e) => {
                    setFilter("dateTo", e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label mb-1 small fw-semibold text-muted">Entry Type</label>
                <select
                  className={`form-select ${buttonClass}`}
                  value={filters.entryType}
                  onChange={(e) => {
                    setFilter("entryType", e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All</option>
                  {ENTRY_TYPES.filter(Boolean).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label mb-1 small fw-semibold text-muted">Mode</label>
                <select
                  className={`form-select ${buttonClass}`}
                  value={filters.paymentMode}
                  onChange={(e) => {
                    setFilter("paymentMode", e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All</option>
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Receipt Date</th>
                    <th>Plan</th>
                    <th>Card No</th>
                    <th>Customer Name</th>
                    <th>Inst #</th>
                    <th>Month</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Payment Mode</th>
                    <th>Source</th>
                    <th>Remarks</th>
                    {canEdit && <th className="text-end">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={canEdit ? 12 : 11} className="text-center py-5 text-muted">
                        Loading transactions...
                      </td>
                    </tr>
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan={canEdit ? 12 : 11} className="text-center py-5 text-muted">
                        {selectedPlanId
                          ? "No transactions found matching the filters."
                          : "Please select a scheme plan from above."}
                      </td>
                    </tr>
                  ) : (
                    paginated.map((entry) => (
                      <tr key={`${entry.enrollment_id}-${entry.id}`}>
                        <td>{entry.receipt_date?.split("T")[0] || "—"}</td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {entry.plan_name || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold text-dark">#{entry.card_no ?? "—"}</span>
                        </td>
                        <td>
                          <span className="fw-semibold text-dark">{entry.customer_name ?? "—"}</span>
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {entry.installment_no}
                          </span>
                        </td>
                        <td>{entry.for_month || "—"}</td>
                        <td>
                          <EntryTypeBadge type={entry.entry_type} />
                        </td>
                        <td className="fw-bold text-dark">
                          {entry.amount != null ? formatIndianRupee(entry.amount) : "—"}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {entry.payment_mode || "—"}
                          </span>
                        </td>
                        <td>{entry.source || "—"}</td>
                        <td style={{ maxWidth: 160 }} className="text-truncate">
                          {entry.remarks || "—"}
                        </td>
                        {canEdit && (
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end align-items-center">
                              {entry.entry_type === "PAYMENT" && (
                                <button
                                  type="button"
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => setEditEntry(entry)}
                                  title="Edit Entry"
                                >
                                  <Icon icon="solar:pen-bold" width={16} height={16} />
                                </button>
                              )}
                              {(entry.entry_type === "PAYMENT" || entry.entry_type === "SKIP") && (
                                <button
                                  type="button"
                                  className={`btn btn-danger btn-sm ${buttonClass}`}
                                  disabled={deletingId === entry.id}
                                  onClick={() => handleDeleteEntry(entry)}
                                  title="Delete Entry"
                                >
                                  <Icon icon="solar:trash-bin-trash-bold" width={16} height={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalItems > 0 && (
              <Pagination
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={(p) => setCurrentPage(p)}
                onItemsPerPageChange={(l) => {
                  setItemsPerPage(l);
                  setCurrentPage(1);
                }}
                itemsPerPageOptions={[10, 15, 25, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>

      {editEntry && (
        <EntryEditModal
          show={!!editEntry}
          onHide={() => setEditEntry(null)}
          entry={editEntry}
          bankAccounts={bankAccounts}
          valueModeIsGoldWeight={editEntryValueMode}
          onSave={handleEntryUpdate}
        />
      )}
    </div>
  );
}

export default SchemePaymentTransaction;
